
/*
Copyright 2026 iodyne.  All rights reserved.
iodyne Proprietary/Confidental: INTERNAL USE ONLY
*/

import IodyneCloudIcon from '@/components/icons/IodyneCloudIcon';
import NFCStickerImage from '@/components/icons/NFCStickerImage';
import ProDataImage from '@/components/icons/ProDataImage';
import ProMiniImage from '@/components/icons/ProMiniImage';
import { CopyLabel } from '@/components/ui/CopyLabel';
import { useTranslation } from '@/i18n/client_hook';
import { defaultNS } from '@/i18n/settings';
import { FGUID  } from '@/lib/guid/fguid';
import { deviceInfoForFGUID, IodyneModels } from '@/lib/models/devices';
import { createFileRoute, Link } from '@tanstack/react-router';

enum PairingType {
    Apple = "A",
    Google = "G"
}

const DETAILS_BASE_URLS:Record<PairingType, string> = {
    [PairingType.Apple] : "https://found.apple.com/accessory",
    [PairingType.Google] : "https://find-my.googleapis.com/lookup"
}

export const Route = createFileRoute('/device/$fguid/public')({
    component: RouteComponent,
    validateSearch: (search) => ({
        p: search.p as PairingType | undefined ?? undefined,
        ...Object.fromEntries(
            Object.entries(search).filter(([key]) => key.startsWith("$"))
        )
    })
});

function detailsURL(pairedState:PairingType, params:Record<string, any>):string {
    var forwardParams = new URLSearchParams();
    Object.entries(params).filter(([key]) => key.startsWith("$")).forEach(([key, value]) => {
        value && forwardParams.append(key.slice(1), value)
    });
    let details_url:string;
    if(pairedState) {
        details_url = `${DETAILS_BASE_URLS[pairedState]}?${forwardParams.toString()}`

    } else {
        details_url = "https://iodyne.com";
    }

    return details_url;
}

function RouteComponent() {
    const { t } = useTranslation(defaultNS);
    const { fguid:serial_no } = Route.useParams();
    const search = Route.useSearch();

    const pairedState = search.p;

    let fguid:FGUID|undefined;
    try {
        fguid = new FGUID(serial_no);
    } catch (e) { 
        fguid = undefined;
    }
    const device_info = deviceInfoForFGUID(serial_no);

    return <div className="flex flex-col h-screen">
        <div className="flex-1 flex items-center justify-center w-screen">
            <div className="flex flex-col space-y-5">
                {/* Device Details */}
                <div className="flex flex-row min-w-[500px]">
                    {/* Device Image */}
                    <div className="h-[200px] pr-4">
                        {device_info && device_info.model == IodyneModels.ProData && <ProDataImage height={200} tooltipEnable={false} />}
                        {device_info && device_info.model == IodyneModels.ProMini && <ProMiniImage height={200} tooltipEnable={false} />}
                        {device_info && device_info.model == IodyneModels.NFCSticker && <NFCStickerImage height={200} tooltipEnable={false} />}
                    </div>
                    {/* Device Info */}
                    <div className="flex flex-col">
                        {/* Device Name*/}
                        <div className="flex flex-row items-start justify-start">
                            <span className="text-3xl detail-title">
                                {device_info && device_info.model == IodyneModels.ProData && t("name_prodata")}
                                {device_info && device_info.model == IodyneModels.ProMini && t("name_promini")}
                                {device_info && device_info.model == IodyneModels.NFCSticker && t("name_nfc")}
                            </span>
                        </div>
                        <div className="pt-5 flex flex-col space-y-4 font-light">
                            {/* FGUIDs */}
                            <div className="flex flex-col space-y-1">
                                <div className="font-bold text-secondary-foreground-highlight">{t("label_device_serial_no")}:</div>
                                {fguid && <CopyLabel text={fguid.fguid()} />}
                            </div>
                            {device_info && device_info.model == IodyneModels.ProMini && <div className="flex flex-col space-y-1">
                                <div className="font-bold text-secondary-foreground-highlight">{t("label_findmy_serial_no")}:</div>
                                {fguid && <CopyLabel text={fguid.compactFguid()} />}
                            </div>}
                        </div>
                    </div>
                </div>
                <div className="pl-2">
                    {!pairedState && <a href={"https://iodyne.com"} className="text-primary-link underline hover:brightness-125">
                        {t("label_learn_more_device")}
                    </a>}
                    {pairedState && <a 
                        href={detailsURL(pairedState, search)} 
                        className="text-primary-link underline hover:brightness-125">
                        {t("label_learn_more_device")}
                    </a>}
                </div>
            </div>
        </div>
        <div className="flex items-start">
            <Link to="/"><IodyneCloudIcon height={40} className="p-6" /></Link>
        </div>
    </div>
}
